import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './PublicApplicationForm.css';
import { COUNTRY_OPTIONS, DEFAULT_PHONE_COUNTRY, isValidPhoneForCountry } from '../utils/phone';
import { useTranslation } from '../i18n/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';


// Use relative URL - proxy will forward to correct port in dev, same-origin in prod
const API_URL = '/api';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

// Resume upload constraints. These mirror the server-side checks in
// server/utils/resumeUpload.js - the server is the real gatekeeper, this is
// just to give applicants immediate feedback.
const RESUME_MAX_BYTES = 5 * 1024 * 1024; // 5 MB
const RESUME_ACCEPT = 'application/pdf,.pdf';

// Mirrors the server-enforced cap in server/routes/applicants.js (/apply).
// That server-side check is the real boundary - this endpoint has no
// dependency on the applicant ever seeing this button, so a frontend-only
// limit would do nothing to stop a direct API call. This exists purely so
// an applicant using the form gets feedback (the button disappearing) at
// the point they'd hit the cap, rather than only finding out after Submit.
const MAX_REFERENCES = 10;

const HELP_PHONE_DISPLAY = '(407) 765-4075';
const HELP_PHONE_TEL = '+14077654075';

// Radio/checkbox values stored in formData and sent to the server are always
// these fixed English strings, regardless of display language - the admin
// dashboard filters/compares on them exactly. Only the on-screen label is
// translated (via the "options.*" translation keys referenced alongside
// each one below). See translations.js's file-level comment for more detail.
const SOURCE_OPTIONS = [
  { value: 'Company Website', labelKey: 'options.source.companyWebsite' },
  { value: 'Indeed', labelKey: 'options.source.indeed' },
  { value: 'LinkedIn', labelKey: 'options.source.linkedin' },
  { value: 'Referral', labelKey: 'options.source.referral' },
  { value: 'Job Fair', labelKey: 'options.source.jobFair' },
  { value: 'Other', labelKey: 'options.source.other' }
];

const YEARS_EXPERIENCE_OPTIONS = [
  { value: 'No Experience', labelKey: 'options.yearsExperience.noExperience' },
  { value: 'Less than one year', labelKey: 'options.yearsExperience.lessThanOneYear' },
  { value: '1 to 3 years', labelKey: 'options.yearsExperience.oneToThreeYears' },
  { value: '4 or more years', labelKey: 'options.yearsExperience.fourOrMoreYears' }
];

const EDUCATION_LEVEL_OPTIONS = [
  { value: 'High School or GED', labelKey: 'options.educationLevel.highSchool' },
  { value: 'Some College', labelKey: 'options.educationLevel.someCollege' },
  { value: 'Associates', labelKey: 'options.educationLevel.associates' },
  { value: 'Bachelors', labelKey: 'options.educationLevel.bachelors' },
  { value: 'Masters', labelKey: 'options.educationLevel.masters' }
];

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: 'Full time', labelKey: 'options.employmentType.fullTime' },
  { value: 'Part time', labelKey: 'options.employmentType.partTime' },
  { value: 'Temporary', labelKey: 'options.employmentType.temporary' }
];

const SHIFT_PREFERENCE_OPTIONS = [
  { value: 'Days', labelKey: 'options.shiftPreference.days' },
  { value: 'Afternoons', labelKey: 'options.shiftPreference.afternoons' },
  { value: 'Nights', labelKey: 'options.shiftPreference.nights' }
];

const DAY_OPTIONS = [
  { value: 'All', labelKey: 'options.days.all' },
  { value: 'Monday', labelKey: 'options.days.monday' },
  { value: 'Tuesday', labelKey: 'options.days.tuesday' },
  { value: 'Wednesday', labelKey: 'options.days.wednesday' },
  { value: 'Thursday', labelKey: 'options.days.thursday' },
  { value: 'Friday', labelKey: 'options.days.friday' },
  { value: 'Saturday', labelKey: 'options.days.saturday' },
  { value: 'Sunday', labelKey: 'options.days.sunday' },
  { value: 'Holidays', labelKey: 'options.days.holidays' }
];

const CHANGING_SCHEDULE_OPTIONS = [
  { value: 'Yes', labelKey: 'options.changingSchedule.yes' },
  { value: 'No', labelKey: 'options.changingSchedule.no' },
  { value: 'Occasionally', labelKey: 'options.changingSchedule.occasionally' }
];

const START_DATE_OPTIONS = [
  { value: 'Immediately', labelKey: 'options.startDate.immediately' },
  { value: '1-2 weeks', labelKey: 'options.startDate.oneToTwoWeeks' },
  { value: 'Over 3 weeks', labelKey: 'options.startDate.overThreeWeeks' }
];

function PublicApplicationForm() {
  const { t } = useTranslation();

  const [currentStep, setCurrentStep] = useState(1);
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeError, setResumeError] = useState('');
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [positionSearch, setPositionSearch] = useState('');
  const [positionPage, setPositionPage] = useState(1);
  const [itemsPerPage] = useState(30);
  const [descriptionModal, setDescriptionModal] = useState(null);
  // Per-field messages, shown directly under the offending input rather than
  // only as a banner at the top of the form. Keyed by formData field name
  // (e.g. "phone", "city") - or, for a reference, by "references.<index>.<field>".
  const [fieldErrors, setFieldErrors] = useState({});

  const [formData, setFormData] = useState({
    // Position
    position_id: '',
    source: 'Company Website',
    
    // Contact Info
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    phone_country: DEFAULT_PHONE_COUNTRY,
    address: '',

    street2: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States',
    
    // General Questions
    worked_before: '',
    years_experience: '',
    at_least_18: '',
    has_fl_license: '',
    felony_conviction: '',
    military_service: '',
    education_level: '',
    
    // Schedule
    employment_type: '',
    shift_preference: '',
    available_days: [],
    changing_schedule: '',
    start_date: '',
    
    // References
    has_references: '',
    references: []
  });

  useEffect(() => {
    loadOpenPositions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadOpenPositions = async () => {
    try {
      const response = await axios.get(`${API_URL}/positions/public`);
      setPositions(response.data);
    } catch (err) {
      console.error('Error loading positions:', err);
      setError(t('errors.loadPositionsFailed'));
    } finally {
      setLoading(false);
    }
  };

  // The browser's own type="email"/type="tel" checks never run on this form,
  // because steps advance via an onClick handler rather than a real form
  // submit. So these formats have to be checked explicitly.
  //
  // Mirrors the server rules in POST /applicants/apply. The server is still
  // the authority; this just gives immediate feedback at the field.
  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());

  // A fixed "10-15 digits" rule is not a real validation rule for any
  // country - it let through non-existent numbers (e.g. a US number with a
  // 555 area code) and had no basis for applicants outside the US.
  // isValidPhoneForCountry checks against that specific country's actual
  // numbering plan via libphonenumber-js.
  const isValidPhone = (value, country) => isValidPhoneForCountry(value, country);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear the message as soon as they start correcting it, so the field
    // does not stay red while they are mid-fix.
    setFieldErrors(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });

    if (name === 'position_id') {
      const position = positions.find(p => p.id === parseInt(value));
      setSelectedPosition(position);
    }

    if (name === 'has_references' && value === 'Yes') {
      setFormData(prev => (
        prev.references.length === 0
          ? { ...prev, references: [{ name: '', phone: '', phone_country: DEFAULT_PHONE_COUNTRY, email: '', relationship: '' }] }
          : prev
      ));
    }
  };

  // Validate when they leave the field - the usual form behaviour. Empty is
  // not flagged here; that is handled by the required-field check on Next,
  // so applicants are not scolded for a field they have not reached yet.
  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (!value) return;

    let message = '';
    if (name === 'email' && !isValidEmail(value)) {
      message = t('errors.fields.emailInvalid');
    }
    if (name === 'phone' && !isValidPhone(value, formData.phone_country)) {
      message = t('errors.fields.phoneInvalid');
    }

    if (message) {
      setFieldErrors(prev => ({ ...prev, [name]: message }));
    }
  };


  const errorTextStyle = { color: '#b91c1c', marginTop: '6px', fontSize: '13px' };

  // Which fieldErrors keys belong to each step, so the generic banner above
  // Next/Submit (see render, near form-navigation) can tell whether *this*
  // step still has a problem - without needing its own separate state that
  // could drift out of sync with fieldErrors as individual fields get fixed.
  const STEP_FIELD_KEYS = {
    1: ['position_id'],
    2: ['first_name', 'last_name', 'email', 'phone', 'city', 'state', 'zip'],
    3: ['worked_before', 'years_experience', 'at_least_18', 'has_fl_license', 'felony_conviction', 'military_service', 'education_level'],
    4: ['employment_type', 'shift_preference', 'available_days', 'changing_schedule', 'start_date'],
    5: ['has_references'], // plus any "references.*" keys, handled below
    6: []
  };

  const currentStepHasErrors = (() => {
    // The review step (6) has no fields of its own to validate client-side -
    // nextStep() already blocks advancing past steps 1-5 while any of their
    // own fields are invalid, so the only way a fieldError can exist while
    // sitting on step 6 is a server-side rejection on submit (e.g. a bad
    // reference phone, or the position having closed between page load and
    // submit). Any leftover entry at all is therefore relevant here.
    if (currentStep === 6) {
      return Object.keys(fieldErrors).length > 0;
    }

    const keys = STEP_FIELD_KEYS[currentStep] || [];
    if (keys.some((key) => fieldErrors[key])) return true;
    if (currentStep === 5) {
      return Object.keys(fieldErrors).some((key) => key.startsWith('references.'));
    }
    return false;
  })();

  const handleCheckboxChange = (field, value) => {
    setFormData(prev => {
      const current = prev[field];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
    // Same as handleInputChange: clear a stale error as soon as they act on it.
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const addReference = () => {
    setFormData(prev => ({
      ...prev,
      references: [...prev.references, { name: '', phone: '', phone_country: DEFAULT_PHONE_COUNTRY, email: '', relationship: '' }]
    }));
  };


  const removeReference = (index) => {
    setFormData(prev => ({
      ...prev,
      references: prev.references.filter((_, i) => i !== index)
    }));
    setFieldErrors(prev => {
      const next = {};
      Object.keys(prev).forEach((key) => {
        if (!key.startsWith(`references.${index}.`)) next[key] = prev[key];
      });
      return next;
    });
  };

  const updateReference = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      references: prev.references.map((ref, i) => 
        i === index ? { ...ref, [field]: value } : ref
      )
    }));
    const key = `references.${index}.${field}`;
    setFieldErrors(prev => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const renderStep2Old = () => (
    <div className="form-step">
      <h2>{t('contact.title')}</h2>
      
      <div className="form-section">
        <h3>{t('contact.sectionTitle')}</h3>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">{t('contact.firstName')} *</label>
            <input
              type="text"
              name="first_name"
              className="form-input"
              value={formData.first_name}
              onChange={handleInputChange}
              style={fieldErrors.first_name ? { borderColor: '#b91c1c' } : undefined}
              aria-invalid={fieldErrors.first_name ? 'true' : undefined}
              required
            />
            {fieldErrors.first_name && (
              <div style={errorTextStyle}>{fieldErrors.first_name}</div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">{t('contact.lastName')} *</label>
            <input
              type="text"
              name="last_name"
              className="form-input"
              value={formData.last_name}
              onChange={handleInputChange}
              style={fieldErrors.last_name ? { borderColor: '#b91c1c' } : undefined}
              aria-invalid={fieldErrors.last_name ? 'true' : undefined}
              required
            />
            {fieldErrors.last_name && (
              <div style={errorTextStyle}>{fieldErrors.last_name}</div>
            )}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">{t('contact.email')} *</label>
            <input
              type="email"
              name="email"
              className="form-input"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleBlur}
              style={fieldErrors.email ? { borderColor: '#b91c1c' } : undefined}
              aria-invalid={fieldErrors.email ? 'true' : undefined}
              required
            />
            {fieldErrors.email && (
              <div style={errorTextStyle}>{fieldErrors.email}</div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">{t('contact.phone')} *</label>
            <div className="phone-input-row">
              <select
                name="phone_country"
                className="form-select phone-country-select"
                value={formData.phone_country}
                onChange={handleInputChange}
                aria-label={t('contact.phoneCountryLabel')}
              >
                {COUNTRY_OPTIONS.map(({ code, name }) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
              <input
                type="tel"
                name="phone"
                className="form-input"
                value={formData.phone}
                onChange={handleInputChange}
                onBlur={handleBlur}
                style={fieldErrors.phone ? { borderColor: '#b91c1c' } : undefined}
                aria-invalid={fieldErrors.phone ? 'true' : undefined}
                required
              />
            </div>
            {fieldErrors.phone && (
              <div style={errorTextStyle}>{fieldErrors.phone}</div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">{t('contact.streetAddress')}</label>
          <input
            type="text"
            name="address"
            className="form-input"
            value={formData.address}
            onChange={handleInputChange}
          />
        </div>

        <div className="form-group">
          <label className="form-label">{t('contact.streetAddress2')}</label>
          <input
            type="text"
            name="street2"
            className="form-input"
            value={formData.street2}
            onChange={handleInputChange}
            placeholder={t('contact.streetAddress2Placeholder')}
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="form-label">{t('contact.city')} *</label>
            <input
              type="text"
              name="city"
              className="form-input"
              value={formData.city}
              onChange={handleInputChange}
              style={fieldErrors.city ? { borderColor: '#b91c1c' } : undefined}
              aria-invalid={fieldErrors.city ? 'true' : undefined}
              required
            />
            {fieldErrors.city && (
              <div style={errorTextStyle}>{fieldErrors.city}</div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">{t('contact.state')} *</label>
            <select
              name="state"
              className="form-select"
              value={formData.state}
              onChange={handleInputChange}
              style={fieldErrors.state ? { borderColor: '#b91c1c' } : undefined}
              aria-invalid={fieldErrors.state ? 'true' : undefined}
              required
            >
              <option value="">{t('contact.selectState')}</option>
              {US_STATES.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            {fieldErrors.state && (
              <div style={errorTextStyle}>{fieldErrors.state}</div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">{t('contact.zip')} *</label>
            <input
              type="text"
              name="zip"
              className="form-input"
              value={formData.zip}
              onChange={handleInputChange}
              maxLength="10"
              style={fieldErrors.zip ? { borderColor: '#b91c1c' } : undefined}
              aria-invalid={fieldErrors.zip ? 'true' : undefined}
              required
            />
            {fieldErrors.zip && (
              <div style={errorTextStyle}>{fieldErrors.zip}</div>
            )}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">{t('contact.country')}</label>
          <input
            type="text"
            name="country"
            className="form-input"
            value={formData.country}
            readOnly
          />
        </div>
      </div>
    </div>
  );

  // Returns a map of { fieldName: message } for every invalid/missing
  // required field on `step`, or {} when the step is good. Replaces the old
  // single blanket "fill in all required fields" message with one message
  // per actual offending field, so each is shown right under that field
  // instead of applicants having to guess (and scroll up) which field was
  // the problem.
  const getStepFieldErrors = (step) => {
    const errs = {};

    switch (step) {
      case 1:
        if (!formData.position_id) errs.position_id = t('errors.fields.position');
        break;

      case 2: {
        if (!formData.first_name) errs.first_name = t('errors.fields.firstName');
        if (!formData.last_name) errs.last_name = t('errors.fields.lastName');

        if (!formData.email) {
          errs.email = t('errors.fields.emailRequired');
        } else if (!isValidEmail(formData.email)) {
          errs.email = t('errors.fields.emailInvalid');
        }

        if (!formData.phone) {
          errs.phone = t('errors.fields.phoneRequired');
        } else if (!isValidPhone(formData.phone, formData.phone_country)) {
          errs.phone = t('errors.fields.phoneInvalid');
        }

        if (!formData.city) errs.city = t('errors.fields.city');
        if (!formData.state) errs.state = t('errors.fields.state');
        if (!formData.zip) errs.zip = t('errors.fields.zip');
        break;
      }

      case 3:
        if (!formData.worked_before) errs.worked_before = t('errors.fields.workedBefore');
        if (!formData.years_experience) errs.years_experience = t('errors.fields.yearsExperience');
        if (!formData.at_least_18) errs.at_least_18 = t('errors.fields.atLeast18');
        if (!formData.has_fl_license) errs.has_fl_license = t('errors.fields.hasFlLicense');
        if (!formData.felony_conviction) errs.felony_conviction = t('errors.fields.felonyConviction');
        if (!formData.military_service) errs.military_service = t('errors.fields.militaryService');
        if (!formData.education_level) errs.education_level = t('errors.fields.educationLevel');
        break;

      case 4:
        if (!formData.employment_type) errs.employment_type = t('errors.fields.employmentType');
        if (!formData.shift_preference) errs.shift_preference = t('errors.fields.shiftPreference');
        if (formData.available_days.length === 0) errs.available_days = t('errors.fields.availableDays');
        if (!formData.changing_schedule) errs.changing_schedule = t('errors.fields.changingSchedule');
        if (!formData.start_date) errs.start_date = t('errors.fields.startDate');
        break;

      case 5: {
        if (!formData.has_references) {
          errs.has_references = t('errors.fields.hasReferences');
          break;
        }

        if (formData.has_references === 'Yes') {
          if (formData.references.length === 0) {
            errs.has_references = t('errors.references.incomplete');
            break;
          }

          formData.references.forEach((ref, index) => {
            if (!ref.name) {
              errs[`references.${index}.name`] = t('errors.references.incomplete');
            }
            if (!ref.phone) {
              errs[`references.${index}.phone`] = t('errors.references.incomplete');
            } else if (!isValidPhone(ref.phone, ref.phone_country)) {
              errs[`references.${index}.phone`] = t('errors.references.invalidPhone');
            }
            if (ref.email && !isValidEmail(ref.email)) {
              errs[`references.${index}.email`] = t('errors.references.invalidEmail');
            }
          });
        }
        break;
      }

      case 6:
        break; // Review step - nothing to validate here directly

      default:
        break;
    }

    return errs;
  };

  const nextStep = () => {
    const stepErrors = getStepFieldErrors(currentStep);

    if (Object.keys(stepErrors).length > 0) {
      // Each message is already shown directly under its own field (see
      // fieldErrors rendering throughout this file), so a generic top
      // banner here would just be redundant - and worse, it would be
      // frozen in whatever language was active the moment this ran,
      // since it is stored as already-resolved text rather than a key
      // re-translated on every render like the field messages are.
      setFieldErrors(prev => ({ ...prev, ...stepErrors }));
      return;
    }

    setCurrentStep(prev => Math.min(prev + 1, 6));
    setError('');
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    setError('');
  };

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0];
    setResumeError('');

    if (!file) {
      setResumeFile(null);
      return;
    }

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setResumeFile(null);
      setResumeError(t('errors.resumeNotPdf'));
      e.target.value = '';
      return;
    }

    if (file.size > RESUME_MAX_BYTES) {
      setResumeFile(null);
      setResumeError(t('errors.resumeTooLarge'));
      e.target.value = '';
      return;
    }

    setResumeFile(file);
  };

  // Maps a server error item ({ code, msg, path, vars }) to localized text.
  // Falls back to whatever English text the server sent if the code is not
  // recognized (e.g. an older cached client bundle vs. a newer server), so
  // an error is never silently swallowed - just possibly shown in English.
  const mapServerError = (item) => {
    if (item.code) {
      return t(`serverErrors.${item.code}`, item.vars);
    }
    return item.msg || t('errors.generic');
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    try {
      // Sent as multipart/form-data so the optional resume file can ride along
      // with the rest of the application in a single request.
      const payload = new FormData();

      const fields = {
        ...formData,
        position_id: parseInt(formData.position_id),
        available_days: JSON.stringify(formData.available_days),
        references_data: formData.has_references === 'Yes' ? JSON.stringify(formData.references) : null
      };

      // FormData sends everything as strings; skip null/undefined so the
      // server sees them as absent rather than the text "null".
      Object.entries(fields).forEach(([key, value]) => {
        if (key === 'references') return; // superseded by references_data
        if (value === null || value === undefined) return;
        payload.append(key, value);
      });

      if (resumeFile) {
        payload.append('resume', resumeFile);
      }

      await axios.post(`${API_URL}/applicants/apply`, payload);

      setSuccess(true);
      setCurrentStep(1);
      setResumeFile(null);
      setResumeError('');
      setFieldErrors({});
      setFormData({
        position_id: '',
        source: 'Company Website',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        phone_country: DEFAULT_PHONE_COUNTRY,
        address: '',
        street2: '',
        city: '',
        state: '',
        zip: '',
        country: 'United States',
        worked_before: '',

        years_experience: '',
        at_least_18: '',
        has_fl_license: '',
        felony_conviction: '',
        military_service: '',
        education_level: '',
        employment_type: '',
        shift_preference: '',
        available_days: [],
        changing_schedule: '',
        start_date: '',
        has_references: '',
        references: []
      });
      setSelectedPosition(null);
    } catch (err) {
      // express-validator replies with { errors: [{ msg, path }] }, while other
      // failures reply with { error: "..." }. The server now also sends a
      // stable "code" alongside each error so the message can be localized
      // (see mapServerError above) rather than shown verbatim in English.
      const data = err.response?.data;

      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        const mapped = {};
        data.errors.forEach((item) => {
          const field = item.path || item.param;
          if (field) mapped[field] = mapServerError(item);
        });
        setFieldErrors(prev => ({ ...prev, ...mapped }));

        // Contact fields live on step 2, so send them back there to fix it
        // in place rather than leaving them stranded on the review step.
        // No top banner here either, for the same reason as nextStep().
        if (mapped.email || mapped.phone || mapped.first_name || mapped.last_name ||
            mapped.city || mapped.state || mapped.zip) {
          setCurrentStep(2);
        }
      } else if (data?.code) {
        setError(t(`serverErrors.${data.code}`));
      } else {
        setError(data?.error || t('errors.submitFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="public-app-container">
        <div className="loading">{t('loading')}</div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="public-app-container">
        <div className="success-message">
          <div className="success-icon">✓</div>
          <h2>{t('success.title')}</h2>
          <p>{t('success.body')}</p>
          <button 
            className="btn btn-primary"
            onClick={() => setSuccess(false)}
          >
            {t('success.button')}
          </button>
        </div>
      </div>
    );
  }

  const renderStepIndicator = () => (
    <div className="step-indicator">
      {[1, 2, 3, 4, 5, 6].map(step => (
        <div key={step} className={`step ${currentStep === step ? 'active' : currentStep > step ? 'completed' : ''}`}>
          <div className="step-number">{step}</div>
          <div className="step-label">
            {step === 1 && t('steps.position')}
            {step === 2 && t('steps.contact')}
            {step === 3 && t('steps.general')}
            {step === 4 && t('steps.schedule')}
            {step === 5 && t('steps.references')}
            {step === 6 && t('steps.review')}
          </div>
        </div>
      ))}
    </div>
  );

  const renderRadioGroup = (name, options, value) => (
    <div className="radio-group">
      {options.map(({ value: optValue, labelKey }) => (
        <label key={optValue}>
          <input
            type="radio"
            name={name}
            value={optValue}
            checked={value === optValue}
            onChange={handleInputChange}
          />
          <span>{t(labelKey)}</span>
        </label>
      ))}
    </div>
  );

  const renderStep2 = () => (
    <div className="form-step">
      <h2>{t('general.title')}</h2>
      
      <div className="form-section">
        <div className="form-group">
          <label className="form-label">{t('general.workedBefore')} *</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="worked_before"
                value="Yes"
                checked={formData.worked_before === 'Yes'}
                onChange={handleInputChange}
              />
              <span>{t('common.yes')}</span>
            </label>
            <label>
              <input
                type="radio"
                name="worked_before"
                value="No"
                checked={formData.worked_before === 'No'}
                onChange={handleInputChange}
              />
              <span>{t('common.no')}</span>
            </label>
          </div>
          {fieldErrors.worked_before && (
            <div style={errorTextStyle}>{fieldErrors.worked_before}</div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('general.yearsExperience')} *</label>
          {renderRadioGroup('years_experience', YEARS_EXPERIENCE_OPTIONS, formData.years_experience)}
          {fieldErrors.years_experience && (
            <div style={errorTextStyle}>{fieldErrors.years_experience}</div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('general.atLeast18')} *</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="at_least_18"
                value="Yes"
                checked={formData.at_least_18 === 'Yes'}
                onChange={handleInputChange}
              />
              <span>{t('common.yes')}</span>
            </label>
            <label>
              <input
                type="radio"
                name="at_least_18"
                value="No"
                checked={formData.at_least_18 === 'No'}
                onChange={handleInputChange}
              />
              <span>{t('common.no')}</span>
            </label>
          </div>
          {fieldErrors.at_least_18 && (
            <div style={errorTextStyle}>{fieldErrors.at_least_18}</div>
          )}

          {/* Applicants under 18 were never told a parent/guardian would need
              to authorize their employment - it surfaced as a surprise later
              in the process instead. Shown as soon as they answer No, and
              repeated on the review step. */}
          {formData.at_least_18 === 'No' && (
            <div className="form-notice">
              <strong>{t('general.title')}:</strong> {t('general.under18Notice')}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('general.hasFlLicense')} *</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="has_fl_license"
                value="Yes"
                checked={formData.has_fl_license === 'Yes'}
                onChange={handleInputChange}
              />
              <span>{t('common.yes')}</span>
            </label>
            <label>
              <input
                type="radio"
                name="has_fl_license"
                value="No"
                checked={formData.has_fl_license === 'No'}
                onChange={handleInputChange}
              />
              <span>{t('common.no')}</span>
            </label>
          </div>
          {fieldErrors.has_fl_license && (
            <div style={errorTextStyle}>{fieldErrors.has_fl_license}</div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('general.felonyConviction')} *</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="felony_conviction"
                value="Yes"
                checked={formData.felony_conviction === 'Yes'}
                onChange={handleInputChange}
              />
              <span>{t('common.yes')}</span>
            </label>
            <label>
              <input
                type="radio"
                name="felony_conviction"
                value="No"
                checked={formData.felony_conviction === 'No'}
                onChange={handleInputChange}
              />
              <span>{t('common.no')}</span>
            </label>
          </div>
          {fieldErrors.felony_conviction && (
            <div style={errorTextStyle}>{fieldErrors.felony_conviction}</div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('general.militaryService')} *</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="military_service"
                value="Yes"
                checked={formData.military_service === 'Yes'}
                onChange={handleInputChange}
              />
              <span>{t('common.yes')}</span>
            </label>
            <label>
              <input
                type="radio"
                name="military_service"
                value="No"
                checked={formData.military_service === 'No'}
                onChange={handleInputChange}
              />
              <span>{t('common.no')}</span>
            </label>
          </div>
          {fieldErrors.military_service && (
            <div style={errorTextStyle}>{fieldErrors.military_service}</div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('general.educationLevel')} *</label>
          {renderRadioGroup('education_level', EDUCATION_LEVEL_OPTIONS, formData.education_level)}
          {fieldErrors.education_level && (
            <div style={errorTextStyle}>{fieldErrors.education_level}</div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="form-step">
      <h2>{t('references.title')}</h2>
      
      <div className="form-section">
        <div className="form-group">
          <label className="form-label">{t('references.hasReferences')} *</label>
          <div className="radio-group">
            <label>
              <input
                type="radio"
                name="has_references"
                value="Yes"
                checked={formData.has_references === 'Yes'}
                onChange={handleInputChange}
              />
              <span>{t('common.yes')}</span>
            </label>
            <label>
              <input
                type="radio"
                name="has_references"
                value="No"
                checked={formData.has_references === 'No'}
                onChange={handleInputChange}
              />
              <span>{t('common.no')}</span>
            </label>
          </div>
          {fieldErrors.has_references && (
            <div style={errorTextStyle}>{fieldErrors.has_references}</div>
          )}
        </div>

        {formData.has_references === 'Yes' && (
          <div>
            {formData.references.map((ref, index) => (
              <div key={index} className="reference-group">
                <div className="reference-header">
                  <h4>{t('references.referenceNumber', { index: index + 1 })}</h4>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => removeReference(index)}
                  >
                    {t('references.remove')}
                  </button>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('references.name')} *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={ref.name}
                      onChange={(e) => updateReference(index, 'name', e.target.value)}
                      style={fieldErrors[`references.${index}.name`] ? { borderColor: '#b91c1c' } : undefined}
                      required
                    />
                    {fieldErrors[`references.${index}.name`] && (
                      <div style={errorTextStyle}>{fieldErrors[`references.${index}.name`]}</div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('references.phoneNumber')} *</label>
                    <div className="phone-input-row">
                      <select
                        className="form-select phone-country-select"
                        value={ref.phone_country || DEFAULT_PHONE_COUNTRY}
                        onChange={(e) => updateReference(index, 'phone_country', e.target.value)}
                        aria-label={t('references.referenceNumber', { index: index + 1 }) + ' ' + t('contact.phoneCountryLabel')}
                      >
                        {COUNTRY_OPTIONS.map(({ code, name }) => (
                          <option key={code} value={code}>{name}</option>
                        ))}
                      </select>
                      <input
                        type="tel"
                        className="form-input"
                        value={ref.phone}
                        onChange={(e) => updateReference(index, 'phone', e.target.value)}
                        style={fieldErrors[`references.${index}.phone`] ? { borderColor: '#b91c1c' } : undefined}
                        required
                      />
                    </div>
                    {fieldErrors[`references.${index}.phone`] && (
                      <div style={errorTextStyle}>{fieldErrors[`references.${index}.phone`]}</div>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{t('references.email')}</label>
                    <input
                      type="email"
                      className="form-input"
                      value={ref.email}
                      onChange={(e) => updateReference(index, 'email', e.target.value)}
                      style={fieldErrors[`references.${index}.email`] ? { borderColor: '#b91c1c' } : undefined}
                    />
                    {fieldErrors[`references.${index}.email`] && (
                      <div style={errorTextStyle}>{fieldErrors[`references.${index}.email`]}</div>
                    )}
                  </div>
                  <div className="form-group">
                    <label className="form-label">{t('references.relationship')}</label>
                    <input
                      type="text"
                      className="form-input"
                      value={ref.relationship}
                      onChange={(e) => updateReference(index, 'relationship', e.target.value)}
                      placeholder={t('references.relationshipPlaceholder')}
                    />
                  </div>
                </div>
              </div>
            ))}

            {formData.references.length < MAX_REFERENCES && (
              <button
                type="button"
                className="btn btn-outline"
                onClick={addReference}
                style={{marginTop: '16px'}}
              >
                {t('references.addAnother')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Moved here from the review step so applicants attach a resume as
          part of filling out the form, rather than encountering it for the
          first time on the final review screen. */}
      <div className="form-section">
        <h3>{t('references.resumeTitle')}</h3>
        <p style={{margin: '0 0 12px 0', color: '#64748b', fontSize: '14px'}}>
          {t('references.resumeDescription')}
        </p>

        <input
          type="file"
          accept={RESUME_ACCEPT}
          onChange={handleResumeChange}
          disabled={submitting}
        />

        {resumeError && (
          <div style={{color: '#b91c1c', marginTop: '8px', fontSize: '14px'}}>
            {resumeError}
          </div>
        )}

        {resumeFile && !resumeError && (
          <div style={{marginTop: '8px', fontSize: '14px'}}>
            {t('references.attached')} <strong>{resumeFile.name}</strong>{' '}
            <button
              type="button"
              onClick={() => setResumeFile(null)}
              style={{
                marginLeft: '8px',
                background: 'none',
                border: 'none',
                color: '#b91c1c',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0,
                fontSize: '14px'
              }}
            >
              {t('references.removeFile')}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderStep5 = () => {

    const pos = positions.find(p => p.id === parseInt(formData.position_id));
    
    return (
      <div className="form-step">
        <h2>{t('review.title')}</h2>
        <p style={{marginBottom: '24px', color: '#64748b'}}>
          {t('review.subtitle')}
        </p>
        
        <div className="review-section">
          <h3>{t('review.positionSource')}</h3>
          <div className="review-item">
            <strong>{t('review.position')}</strong> {pos?.title} - {pos?.location}
          </div>
          <div className="review-item">
            <strong>{t('review.howYouHeard')}</strong> {t(SOURCE_OPTIONS.find(o => o.value === formData.source)?.labelKey || '') || formData.source}
          </div>
        </div>

        <div className="review-section">
          <h3>{t('review.contactInformation')}</h3>

          <div className="review-item">
            <strong>{t('review.name')}</strong> {formData.first_name} {formData.last_name}
          </div>
          <div className="review-item">
            <strong>{t('review.email')}</strong> {formData.email}
          </div>
          <div className="review-item">
            <strong>{t('review.phone')}</strong> {formData.phone}
            {' '}
            ({COUNTRY_OPTIONS.find((c) => c.code === formData.phone_country)?.name || formData.phone_country})
          </div>

          <div className="review-item">
            <strong>{t('review.address')}</strong> {formData.address}{formData.street2 && `, ${formData.street2}`}, {formData.city}, {formData.state} {formData.zip}
          </div>
        </div>

        <div className="review-section">
          <h3>{t('review.generalQuestions')}</h3>
          <div className="review-item">
            <strong>{t('review.workedHereBefore')}</strong> {formData.worked_before === 'Yes' ? t('common.yes') : formData.worked_before === 'No' ? t('common.no') : ''}
          </div>
          <div className="review-item">
            <strong>{t('review.yearsOfExperience')}</strong> {t(YEARS_EXPERIENCE_OPTIONS.find(o => o.value === formData.years_experience)?.labelKey || '') || formData.years_experience}
          </div>
          <div className="review-item">
            <strong>{t('review.atLeast18YearsOld')}</strong> {formData.at_least_18 === 'Yes' ? t('common.yes') : formData.at_least_18 === 'No' ? t('common.no') : ''}
          </div>
          {formData.at_least_18 === 'No' && (
            <div className="form-notice">
              <strong>{t('general.title')}:</strong> {t('review.under18Notice')}
            </div>
          )}
          <div className="review-item">
            <strong>{t('review.floridaLicense')}</strong> {formData.has_fl_license === 'Yes' ? t('common.yes') : formData.has_fl_license === 'No' ? t('common.no') : ''}
          </div>
          <div className="review-item">
            <strong>{t('review.felonyConviction')}</strong> {formData.felony_conviction === 'Yes' ? t('common.yes') : formData.felony_conviction === 'No' ? t('common.no') : ''}
          </div>
          <div className="review-item">
            <strong>{t('review.militaryService')}</strong> {formData.military_service === 'Yes' ? t('common.yes') : formData.military_service === 'No' ? t('common.no') : ''}
          </div>
          <div className="review-item">
            <strong>{t('review.highestEducation')}</strong> {t(EDUCATION_LEVEL_OPTIONS.find(o => o.value === formData.education_level)?.labelKey || '') || formData.education_level}
          </div>
        </div>

        <div className="review-section">
          <h3>{t('review.schedulePreferences')}</h3>
          <div className="review-item">
            <strong>{t('review.employmentType')}</strong> {t(EMPLOYMENT_TYPE_OPTIONS.find(o => o.value === formData.employment_type)?.labelKey || '') || formData.employment_type}
          </div>
          <div className="review-item">
            <strong>{t('review.shiftPreference')}</strong> {t(SHIFT_PREFERENCE_OPTIONS.find(o => o.value === formData.shift_preference)?.labelKey || '') || formData.shift_preference}
          </div>
          <div className="review-item">
            <strong>{t('review.availableDays')}</strong> {formData.available_days.map(d => t(DAY_OPTIONS.find(o => o.value === d)?.labelKey || '') || d).join(', ')}
          </div>
          <div className="review-item">
            <strong>{t('review.changingSchedule')}</strong> {t(CHANGING_SCHEDULE_OPTIONS.find(o => o.value === formData.changing_schedule)?.labelKey || '') || formData.changing_schedule}
          </div>
          <div className="review-item">
            <strong>{t('review.earliestStartDate')}</strong> {t(START_DATE_OPTIONS.find(o => o.value === formData.start_date)?.labelKey || '') || formData.start_date}
          </div>
        </div>

        <div className="review-section">
          <h3>{t('review.references')}</h3>
          {formData.has_references === 'Yes' && formData.references.length > 0 ? (
            formData.references.map((ref, index) => (
              <div key={index} className="review-item">
                <strong>{t('references.referenceNumber', { index: index + 1 })}:</strong> {ref.name} ({ref.phone}
                {', '}
                {COUNTRY_OPTIONS.find((c) => c.code === ref.phone_country)?.name || ref.phone_country})
                {ref.relationship && ` - ${ref.relationship}`}
              </div>
            ))

          ) : (
            <div className="review-item">{t('review.noReferences')}</div>
          )}
        </div>

        <div className="disclaimer-section">
          <h3>{t('disclaimer.title')}</h3>
          <div className="disclaimer-text">
            <p>
              {t('disclaimer.certification')}
            </p>
            <h4>{t('disclaimer.privacyNoticeTitle')}</h4>
            <p>
              {t('disclaimer.privacyIntro')}
            </p>
            <p>
              {t('disclaimer.privacyPolicyPrefix')}{' '}
              <a href="https://cleantecoutsourcing.com/privacy-policy/" target="_blank" rel="noopener noreferrer">
                https://cleantecoutsourcing.com/privacy-policy/
              </a>
            </p>
            <p>
              {t('disclaimer.termsPrefix')}{' '}
              <a href="https://cleantecoutsourcing.com/terms-conditions/" target="_blank" rel="noopener noreferrer">
                https://cleantecoutsourcing.com/terms-conditions/
              </a>
            </p>
            <p>
              {t('disclaimer.contactPrefix')}{' '}
              <a href="mailto:contact@ctoutsourcing.com">contact@ctoutsourcing.com</a>.
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderStep3 = () => (
    <div className="form-step">
      <h2>{t('schedule.title')}</h2>
      
      <div className="form-section">
        <div className="form-group">
          <label className="form-label">{t('schedule.employmentType')} *</label>
          {renderRadioGroup('employment_type', EMPLOYMENT_TYPE_OPTIONS, formData.employment_type)}
          {fieldErrors.employment_type && (
            <div style={errorTextStyle}>{fieldErrors.employment_type}</div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('schedule.shiftPreference')} *</label>
          {renderRadioGroup('shift_preference', SHIFT_PREFERENCE_OPTIONS, formData.shift_preference)}
          {fieldErrors.shift_preference && (
            <div style={errorTextStyle}>{fieldErrors.shift_preference}</div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('schedule.availableDays')} *</label>
          <div className="checkbox-group">
            {DAY_OPTIONS.map(({ value: day, labelKey }) => (
              <label key={day}>
                <input
                  type="checkbox"
                  checked={formData.available_days.includes(day)}
                  onChange={() => handleCheckboxChange('available_days', day)}
                />
                <span>{t(labelKey)}</span>
              </label>
            ))}
          </div>
          {fieldErrors.available_days && (
            <div style={errorTextStyle}>{fieldErrors.available_days}</div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('schedule.changingSchedule')} *</label>
          {renderRadioGroup('changing_schedule', CHANGING_SCHEDULE_OPTIONS, formData.changing_schedule)}
          {fieldErrors.changing_schedule && (
            <div style={errorTextStyle}>{fieldErrors.changing_schedule}</div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('schedule.startDate')} *</label>
          {renderRadioGroup('start_date', START_DATE_OPTIONS, formData.start_date)}
          {fieldErrors.start_date && (
            <div style={errorTextStyle}>{fieldErrors.start_date}</div>
          )}
        </div>
      </div>
    </div>
  );

  const getFilteredPositions = () => {
    let filtered = [...positions];
    if (positionSearch) {
      filtered = filtered.filter(pos =>
        pos.title.toLowerCase().includes(positionSearch.toLowerCase()) ||
        pos.location?.toLowerCase().includes(positionSearch.toLowerCase())
      );
    }
    return filtered;
  };

  const getPaginatedPositions = () => {
    const filtered = getFilteredPositions();
    const startIndex = (positionPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filtered.slice(startIndex, endIndex);
  };

  const handleSelectPosition = (position) => {
    setSelectedPosition(position);
    setFormData(prev => ({ ...prev, position_id: position.id.toString() }));
    setFieldErrors(prev => {
      if (!prev.position_id) return prev;
      const next = { ...prev };
      delete next.position_id;
      return next;
    });
  };

  const renderStep1 = () => {
    const filteredPositions = getFilteredPositions();
    const paginatedPositions = getPaginatedPositions();
    const totalPages = Math.ceil(filteredPositions.length / itemsPerPage);

    return (
      <div className="form-step">
        <h2>{t('position.title')}</h2>
        <p style={{color: '#64748b', marginBottom: '24px'}}>
          {t('position.subtitle')}
        </p>
        
        <div className="form-section">
          {/* Search Bar */}
          <div className="form-group">
            <input
              type="text"
              className="form-input"
              placeholder={t('position.searchPlaceholder')}
              value={positionSearch}
              onChange={(e) => {
                setPositionSearch(e.target.value);
                setPositionPage(1);
              }}
            />
          </div>

          {fieldErrors.position_id && (
            <div style={errorTextStyle}>{fieldErrors.position_id}</div>
          )}

          {/* Position Grid */}
          <div className="position-grid">
            {paginatedPositions.map(position => (
              <div
                key={position.id}
                className={`position-card ${selectedPosition?.id === position.id ? 'selected' : ''}`}
                onClick={() => handleSelectPosition(position)}
              >
                <div className="position-card-header">
                  <h3>{position.title}</h3>
                  {selectedPosition?.id === position.id && (
                    <span className="selected-badge">{t('position.selected')}</span>
                  )}
                </div>
                <div className="position-card-body">
                  {position.location && (
                    <div className="position-info">
                      <span className="info-icon">📍</span>
                      <span>{position.location}</span>
                    </div>
                  )}
                  {position.rate && (
                    <div className="position-info">
                      <span className="info-icon">💰</span>
                      <span>{position.rate}</span>
                    </div>
                  )}
                  {position.position_status && (
                    <div className="position-info">
                      <span className="info-icon">⏰</span>
                      <span>{position.position_status}</span>
                    </div>
                  )}
                </div>
                {position.description && (
                  <div className="position-card-footer">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDescriptionModal(position);
                      }}
                      style={{width: '100%', fontSize: '12px', padding: '6px 12px'}}
                    >
                      {t('position.viewDescription')}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={() => setPositionPage(prev => Math.max(1, prev - 1))}
                disabled={positionPage === 1}
              >
                {t('position.previousPage')}
              </button>
              <span className="pagination-info">
                {t('position.pageInfo', { page: positionPage, totalPages, count: filteredPositions.length })}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={() => setPositionPage(prev => Math.min(totalPages, prev + 1))}
                disabled={positionPage === totalPages}
              >
                {t('position.nextPage')}
              </button>
            </div>
          )}

          {filteredPositions.length === 0 && (
            <div className="no-results">
              <p>{t('position.noResults', { search: positionSearch })}</p>
            </div>
          )}

          {!selectedPosition && (
            <div className="selection-prompt">
              <p>{t('position.selectionPrompt')}</p>
            </div>
          )}
        </div>

        {selectedPosition && (
          <>
            <div className="form-section" style={{marginTop: '32px', paddingTop: '32px', borderTop: '2px solid #e2e8f0'}}>
              <h3>{t('position.additionalInfo')}</h3>
              <div className="form-group">
                <label className="form-label">{t('position.howDidYouHear')}</label>
                <select
                  name="source"
                  className="form-select"
                  value={formData.source}
                  onChange={handleInputChange}
                >
                  {SOURCE_OPTIONS.map(({ value, labelKey }) => (
                    <option key={value} value={value}>{t(labelKey)}</option>
                  ))}
                </select>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="public-app-container">
      <div className="public-app-logo-bar">
        <img src="/logo.png" alt="Clean Tec Outsourcing" className="public-app-logo" />
      </div>
      <div className="public-app-header">
        <div className="public-app-header-utility-row">
          <a href={`tel:${HELP_PHONE_TEL}`} className="help-phone-link">
            {t('header.needHelp')} {t('header.callUs', { phone: HELP_PHONE_DISPLAY })}
          </a>
          <LanguageSwitcher />
        </div>
        <h1>{t('header.title')}</h1>
        {positions.length > 0 && renderStepIndicator()}
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {positions.length === 0 ? (
        <div className="no-positions">
          <p>{t('noPositions')}</p>
        </div>
      ) : (
        <div className="public-app-form">
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2Old()}
          {currentStep === 3 && renderStep2()}
          {currentStep === 4 && renderStep3()}
          {currentStep === 5 && renderStep4()}
          {currentStep === 6 && renderStep5()}

          {currentStepHasErrors && (
            <div className="alert alert-error" style={{ marginTop: '24px', marginBottom: 0 }}>
              {t('errors.pleaseCorrect')}
            </div>
          )}

          <div className="form-navigation">
            {currentStep > 1 && (
              <button 
                type="button"
                className="btn btn-outline"
                onClick={prevStep}
              >
                {t('nav.previous')}
              </button>
            )}
            <div style={{flex: 1}}></div>
            {currentStep < 6 ? (
              <button 
                type="button"
                className="btn btn-primary"
                onClick={nextStep}
              >
                {t('nav.next')}
              </button>
            ) : (
              <button 
                type="button"
                className="btn btn-primary btn-lg"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? t('nav.submitting') : t('nav.submit')}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Description Modal */}
      {descriptionModal && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth: '600px'}}>
            <div className="modal-header">
              <h3 className="modal-title">{descriptionModal.title}</h3>
              <button onClick={() => setDescriptionModal(null)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{whiteSpace: 'pre-wrap', lineHeight: '1.6'}}>
                {descriptionModal.description}
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setDescriptionModal(null)}>
                {t('position.closeModal')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PublicApplicationForm;
